import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { IconModule, ONEP_ICON_SVG_PATH } from '@onep/ng-uikit-lib/icon';
import { ONEP_OVERLAY_CLASS } from '@onep/ng-uikit-lib/overlay';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AssetUrlPipe } from './pipe/AssetUrlPipe';
import { GraphQLModule } from './graphql/graphql.module';
import { AuthConfigModule } from './auth/auth-config.module';
import { ForbiddenModalSpawnComponent } from './auth/forbidden-modal-spawn/forbidden-modal-spawn.component';
import { ForbiddenModalComponent } from './auth/forbidden-modal/forbidden-modal.component';
import { RefreshSessionComponent } from './auth/refresh-session/refresh-session.component';
import { ModalModule } from '@onep/ng-uikit-lib/ui/modal';
import { ButtonModule } from '@onep/ng-uikit-lib/ui/button';
import { NoOrgaModalComponent } from './organization/no-orga-modal/no-orga-modal.component';
import { CURRENT_ORGANIZATION_MANAGEMENT_SERVICE } from './organization/current-organization-management.service';
import { environment } from '../environments/environment';
import { CurrentOrganizationManagementImplService } from './organization/current-organization-management-impl.service';
import { CurrentOrganizationManagementNoopService } from './organization/current-organization-management-noop.service';
import { NonAdminModalComponent } from './admin/non-admin-modal/non-admin-modal.component';
import { IS_ADMIN_MANAGEMENT_SERVICE } from './admin/admin-management.service';
import { AdminManagementImplService } from './admin/admin-management-impl.service';
import { AdminManagementNoopService } from './admin/admin-management-noop.service';
import { SharedInfoTrackingService } from './tracking/shared-info-tracking.service';
import { SpinnerModule } from '@onep/ng-uikit-lib/ui/spinner';
import {
  GLADY_TRACKING_CONFIGURATION,
  TrackingConfiguration,
} from '@onep/front-common-lib/tracking';
import { Router } from '@angular/router';
import {
  AuthModule,
  AuthService,
  CurrentOrganizationService,
  LanguageService,
  OrganizationModule,
  TranslocoRootModule,
} from '@onep/front-portal-lib';
import { TranslocoService } from '@ngneat/transloco';
import { GridModule } from '@onep/ng-uikit-lib/grid';
import { SelectCompanyComponent } from './manager/select-company/select-company.component';
import { HeaderModule } from '@onep/ng-uikit-lib/ui/header';
import { OptionCardModule } from '@onep/ng-uikit-lib/ui/option-card';
import { Md5 } from 'ts-md5';

export function initialization(
  router: Router,
  authService: AuthService,
  currentOrganizationService: CurrentOrganizationService,
  languageService: LanguageService,
  translocoService: TranslocoService
): () => void {
  return () => {
    translocoService.setActiveLang('fr');
    languageService.lang$.subscribe((lang) => {
      translocoService.setActiveLang(lang);
    });
    router.initialNavigation();
  };
}

@NgModule({
  declarations: [
    AppComponent,
    AssetUrlPipe,
    RefreshSessionComponent,
    ForbiddenModalSpawnComponent,
    ForbiddenModalComponent,
    NoOrgaModalComponent,
    SelectCompanyComponent,
    NonAdminModalComponent,
  ],
  imports: [
    AuthModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    GraphQLModule,
    GridModule.forRoot(),
    HttpClientModule,
    IconModule,
    AuthConfigModule,
    OrganizationModule.forRoot(),
    ButtonModule,
    TranslocoRootModule,
    ModalModule.forRoot(),
    SpinnerModule,
    HeaderModule,
    OptionCardModule,
  ],
  providers: [
    AssetUrlPipe,
    SharedInfoTrackingService,
    Md5,
    { provide: ONEP_ICON_SVG_PATH, useValue: '/common/assets/wedooicons.svg' },
    { provide: ONEP_OVERLAY_CLASS, useValue: 'shared-info-overlay' },
    {
      provide: IS_ADMIN_MANAGEMENT_SERVICE,
      useClass: environment.features.admin
        ? AdminManagementImplService
        : AdminManagementNoopService,
    },
    {
      provide: CURRENT_ORGANIZATION_MANAGEMENT_SERVICE,
      useClass: environment.features.organization
        ? CurrentOrganizationManagementImplService
        : CurrentOrganizationManagementNoopService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialization,
      multi: true,
      deps: [
        Router,
        AuthService,
        CurrentOrganizationService,
        LanguageService,
        TranslocoService,
      ],
    },
    {
      provide: GLADY_TRACKING_CONFIGURATION,
      useValue: {
        environment: environment.gtmConfig?.envName ?? '',
        site: environment.gtmConfig?.site ?? '',
      } as TrackingConfiguration,
    },
  ],
  exports: [AssetUrlPipe],
  bootstrap: [AppComponent],
})
export class AppModule {}
